import { SERVICE_UNAVAILABLE } from 'shared/contants';
import { isChannelRequestTimeout } from 'shared/channels/errors';
import { documentClient } from 'shared/domain/document/documentClient';
import { reportClient } from 'shared/domain/report/reportClient';
import type { SharedResourceRequest } from 'shared/download/fetchSharedResourceSignedRequest';
import type { SharedResourceSignedRequestResult } from 'shared/download/sharedResourceSignedRequest';
import type { SharedResourceType } from './types';

export interface SharedResourceLink {
  resourceId: string | undefined;
  token: string | undefined;
  issueId: string | undefined;
  eventId: string | undefined;
  incomingEmailMessageId: string | undefined;
  linkedDocumentationId: string | undefined;
}

/** The signed URL request of a public link, once the link has what its type needs. */
export function toSharedResourceRequest(
  entityType: SharedResourceType,
  { resourceId, token, issueId, eventId, incomingEmailMessageId, linkedDocumentationId }: SharedResourceLink
): SharedResourceRequest | undefined {
  if (!resourceId || !token) {
    return undefined;
  }
  if (entityType === 'report') {
    return { entityType, reportId: resourceId, token };
  }
  if (!issueId) {
    return undefined;
  }
  return {
    entityType,
    issueId,
    documentId: resourceId,
    token,
    eventId,
    incomingEmailMessageId,
    linkedDocumentationId,
  };
}

/**
 * Asks the service worker for the signed URL of a public link. A timeout
 * rejects, so the caller can fall back to a direct request; any other failure
 * is answered as the server being unavailable.
 */
export async function requestSignedRequestThroughServiceWorker(
  request: SharedResourceRequest
): Promise<SharedResourceSignedRequestResult> {
  try {
    if (request.entityType === 'report') {
      const { entityType: _entityType, ...reportRequest } = request;
      return await reportClient.getSignedUrl(reportRequest);
    }
    const { entityType: _entityType, ...documentRequest } = request;
    return await documentClient.getSignedUrlSecuredByToken(documentRequest);
  } catch (error) {
    if (isChannelRequestTimeout(error)) {
      throw error;
    }
    return { statusCode: SERVICE_UNAVAILABLE };
  }
}
